import { ReactNode, useCallback } from 'react'
import styled from 'styled-components'
import { Box, Card, Checkmark } from '@workwhile/ui'

interface Props {
  id: number
  isSelected: boolean
  onToggle: (id: number) => void
  children: ReactNode
}

const ClickableCard = styled(Card)<{ $isSelected: boolean }>`
  display: block;
  cursor: pointer;

  ${({ theme: { colors }, $isSelected }) => `
    ${
      // Thicken the border without shifting the content
      $isSelected ? `box-shadow: inset 0 0 0 1px ${colors.primary};` : ''
    }

    &:hover {
      border-color: ${colors.primary};
    }

    &:has(input:focus-visible) {
      outline: 2px solid ${colors.primary};
      outline-offset: 2px;
    }
  `}
`

// The whole card acts as the checkbox, so the native input is only kept for
// its keyboard, focus and screen reader behavior
const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

export const SelectableShiftCard = ({
  id,
  isSelected,
  onToggle,
  children,
}: Props) => {
  const handleChange = useCallback(() => onToggle(id), [onToggle, id])

  return (
    <ClickableCard
      as="label"
      shadowless
      $isSelected={isSelected}
      p={4}
      borderColor={isSelected ? 'primary' : 'neutrals.200'}
      backgroundColor={isSelected ? 'primaries.50' : 'white'}
    >
      <HiddenCheckbox checked={isSelected} onChange={handleChange} />
      {isSelected ? (
        <Box position="absolute" top={3} right={3} aria-hidden>
          <Checkmark checked mr={0} />
        </Box>
      ) : null}
      {children}
    </ClickableCard>
  )
}
